import { ChainId } from '@uniswap/sdk-core'

/**
 * Fallback JSON-RPC endpoints.
 * These are used if the integrator does not provide an endpoint, or if the endpoint does not work.
 *
 * MetaMask allows switching to any URL, but displays a warning if it is not on the "Safe" list:
 * https://github.com/MetaMask/metamask-mobile/blob/bdb7f37c90e4fc923881a07fca38d4e77c73a579/app/core/RPCMethods/wallet_addEthereumChain.js#L228-L235
 * https://chainid.network/chains.json
 *
 * These "Safe" URLs are listed first, followed by other fallback URLs, which are taken from chainlist.org.
 */
export const FALLBACK_URLS = {
  [ChainId.MAINNET]: [
    // "Safe" URLs
    'https://cloudflare-eth.com',
    // "Fallback" URLs
    'https://rpc.ankr.com/eth',
    'https://eth-mainnet.public.blastapi.io',
  ],
  [ChainId.GOERLI]: [
    // "Safe" URLs
    'https://rpc.goerli.mudit.blog/',
    // "Fallback" URLs
    'https://rpc.ankr.com/eth_goerli',
  ],
  [ChainId.SEPOLIA]: [
    // "Safe" URLs
    'https://rpc.sepolia.dev/',
    // "Fallback" URLs
    'https://rpc.sepolia.org/',
    'https://rpc2.sepolia.org/',
    'https://rpc.sepolia.online/',
    'https://www.sepoliarpc.space/',
    'https://rpc-sepolia.rockx.com/',
    'https://rpc.bordel.wtf/sepolia',
  ],
  [ChainId.POLYGON]: [
    // "Safe" URLs
    'https://polygon-rpc.com/',
    'https://rpc-mainnet.matic.network',
    'https://matic-mainnet.chainstacklabs.com',
    'https://rpc-mainnet.maticvigil.com',
    'https://rpc-mainnet.matic.quiknode.pro',
    'https://matic-mainnet-full-rpc.bwarelabs.com',
  ],
  [ChainId.POLYGON_MUMBAI]: [
    // "Safe" URLs
    'https://matic-mumbai.chainstacklabs.com',
    'https://rpc-mumbai.maticvigil.com',
    'https://matic-testnet-archive-rpc.bwarelabs.com',
  ],
  [ChainId.ARBITRUM_ONE]: [
    // "Safe" URLs
    'https://arb1.arbitrum.io/rpc',
    // "Fallback" URLs
    'https://arbitrum.public-rpc.com',
  ],
  [ChainId.ARBITRUM_GOERLI]: [
    // "Safe" URLs
    'https://goerli-rollup.arbitrum.io/rpc',
  ],
  [ChainId.OPTIMISM]: [
    // "Safe" URLs
    'https://mainnet.optimism.io/',
    // "Fallback" URLs
    'https://rpc.ankr.com/optimism',
  ],
  [ChainId.OPTIMISM_GOERLI]: [
    // "Safe" URLs
    'https://goerli.optimism.io',
  ],
  [ChainId.CELO]: [
    // "Safe" URLs
    `https://forno.celo.org`,
  ],
  [ChainId.CELO_ALFAJORES]: [
    // "Safe" URLs
    `https://alfajores-forno.celo-testnet.org`,
  ],
  [ChainId.BNB]: [
    // "Safe" URLs
    'https://endpoints.omniatech.io/v1/bsc/mainnet/public',
    'https://bsc-mainnet.gateway.pokt.network/v1/lb/6136201a7bad1500343e248d',
    'https://1rpc.io/bnb',
    'https://bsc-dataseed3.binance.org',
    'https://bsc-dataseed2.defibit.io',
    'https://bsc-dataseed1.ninicoin.io',
    'https://binance.nodereal.io',
    'https://bsc-dataseed4.defibit.io',
    'https://rpc.ankr.com/bsc',
  ],
  [ChainId.AVALANCHE]: [
    // "Safe" URLs
    'https://api.avax.network/ext/bc/C/rpc',
    'https://avalanche-c-chain.publicnode.com',
  ],
  [ChainId.BASE]: [
    // "Safe" URLs
    'https://mainnet.base.org/',
    'https://developer-access-mainnet.base.org/',
    'https://base.gateway.tenderly.co',
    'https://base.publicnode.com',
    // "Fallback" URLs
    'https://1rpc.io/base',
    'https://base.meowrpc.com',
  ],
  [ChainId.DBK]: [
    // "Safe" URLs
    `https://rpc.mainnet.dbkchain.io`,
  ],
  [ChainId.BERACHAIN]: [
    // "Safe" URLs
    `https://rpc.berachain.com`,
  ],
  [ChainId.HYPERLIQUID]: [
    // "Safe" URLs
    `https://rpc.hyperliquid.xyz/evm`,
  ],
  [ChainId.SONIC]: [
    // "Safe" URLs
    `https://rpc.soniclabs.com`,
  ],
  [ChainId.KATANA]: [
    // "Safe" URLs
    `https://rpc.katana.network`,
  ],
  [ChainId.MITOSIS]: [
    // "Safe" URLs
    `https://rpc.mitosis.org`,
  ],
  [ChainId.SOMNIA]: [
    // "Safe" URLs
    `https://api.infra.mainnet.somnia.network`,
  ],
  [ChainId.ZEROG]: [
    // "Safe" URLs
    `https://evmrpc.0g.ai`,
  ],
  [ChainId.PLASMA]: [
    // "Safe" URLs
    `https://rpc.plasma.to`,
  ],
  [ChainId.MONAD_TESTNET]: [
    // "Safe" URLs
    `https://testnet-rpc.monad.xyz`,
  ],
  [ChainId.MONAD]: [
    // "Safe" URLs
    `https://rpc-mainnet.monadinfra.com`,
  ],
  [ChainId.MEGAETH]: [
    // "Safe" URLs
    // `https://rpc-secret-mega.poptyedev.com`,
    // `https://rpc.notoriouslywrong.com/main/evm/4326`,
    // `https://megaeth-mainnet.g.alchemy.com/v2/h5waiX4ganvaRTWu1BLvY`,
    `https://mainnet.megaeth.com/rpc`,
    // `https://rpc.zerion.io/v1/megaeth`,
  ],
  [ChainId.STABLE]: [
    // "Safe" URLs
    // `https://api-stable-mainnet.n.dwellir.com/500b86ab-9b75-4df0-ba58-b8f60e8ad4f9`,
    `https://rpc.stable.xyz`,
  ],
}

/**
 * Known JSON-RPC endpoints.
 * These are the URLs used by the interface when there is not another available source of chain data.
 */
export const RPC_URLS = {
  [ChainId.MAINNET]: [`https://ethereum.keydonix.com/v1/mainnet`, ...FALLBACK_URLS[ChainId.MAINNET]],
  [ChainId.GOERLI]: [`https://ethereum.keydonix.com/v1/goerli`, ...FALLBACK_URLS[ChainId.GOERLI]],
  [ChainId.SEPOLIA]: [...FALLBACK_URLS[ChainId.SEPOLIA]],
  [ChainId.OPTIMISM]: [...FALLBACK_URLS[ChainId.OPTIMISM]],
  [ChainId.OPTIMISM_GOERLI]: [...FALLBACK_URLS[ChainId.OPTIMISM_GOERLI]],
  [ChainId.ARBITRUM_ONE]: [...FALLBACK_URLS[ChainId.ARBITRUM_ONE]],
  [ChainId.ARBITRUM_GOERLI]: [...FALLBACK_URLS[ChainId.ARBITRUM_GOERLI]],
  [ChainId.POLYGON]: [...FALLBACK_URLS[ChainId.POLYGON]],
  [ChainId.POLYGON_MUMBAI]: [...FALLBACK_URLS[ChainId.POLYGON_MUMBAI]],
  [ChainId.CELO]: FALLBACK_URLS[ChainId.CELO],
  [ChainId.CELO_ALFAJORES]: FALLBACK_URLS[ChainId.CELO_ALFAJORES],
  [ChainId.BNB]: [...FALLBACK_URLS[ChainId.BNB]],
  [ChainId.AVALANCHE]: [...FALLBACK_URLS[ChainId.AVALANCHE]],
  [ChainId.BASE]: [...FALLBACK_URLS[ChainId.BASE]],
  [ChainId.DBK]: [...FALLBACK_URLS[ChainId.DBK]],
  [ChainId.BERACHAIN]: [...FALLBACK_URLS[ChainId.BERACHAIN]],
  [ChainId.HYPERLIQUID]: [...FALLBACK_URLS[ChainId.HYPERLIQUID]],
  [ChainId.SONIC]: [...FALLBACK_URLS[ChainId.SONIC]],
  [ChainId.KATANA]: [...FALLBACK_URLS[ChainId.KATANA]],
  [ChainId.MITOSIS]: [...FALLBACK_URLS[ChainId.MITOSIS]],
  [ChainId.SOMNIA]: [...FALLBACK_URLS[ChainId.SOMNIA]],
  [ChainId.ZEROG]: [...FALLBACK_URLS[ChainId.ZEROG]],
  [ChainId.PLASMA]: [...FALLBACK_URLS[ChainId.PLASMA]],
  [ChainId.MONAD_TESTNET]: [...FALLBACK_URLS[ChainId.MONAD_TESTNET]],
  [ChainId.MONAD]: [...FALLBACK_URLS[ChainId.MONAD]],
  [ChainId.MEGAETH]: [...FALLBACK_URLS[ChainId.MEGAETH]],
  [ChainId.STABLE]: [...FALLBACK_URLS[ChainId.STABLE]],
}
